import { createBrowserRouter, Navigate, Outlet, useLocation } from "react-router";
import { SessionProvider, useSession } from "./lib/session.js";
import { ToastProvider } from "./lib/toast.js";
import { Shell } from "./pages/Shell.js";
import { LoginPage } from "./pages/Login.js";
import { SetPasswordPage } from "./pages/SetPassword.js";
import { BranchSelectPage } from "./pages/BranchSelect.js";
import { OrdersPage } from "./pages/Orders.js";
import { ReceptionPage } from "./pages/Reception.js";
import { OrderPage } from "./pages/Order.js";
import { QuoteNewPage, QuotePage, QuotesPage } from "./pages/Quotes.js";
import { PatientPage, PatientsPage } from "./pages/Patients.js";
import { CaptureItemPage, CaptureOrderPage, CapturePage } from "./pages/Capture.js";
import { ValidationOrderPage, ValidationPage } from "./pages/Validation.js";
import { DeliveryOrderPage, DeliveryPage } from "./pages/Delivery.js";
import { CashClosePage } from "./pages/CashClose.js";
import { DoctorReportPage } from "./pages/DoctorReport.js";
import { CataloguePage, StudyPage } from "./pages/Catalogue.js";
import { CalendarPage } from "./pages/Calendar.js";
import { HomePage } from "./pages/Home.js";
import { SettingsPage } from "./pages/Settings.js";
import { MySignaturePage } from "./pages/MySignature.js";
import { PendingSamplesPage } from "./pages/PendingSamples.js";
import { Empty, Loading } from "./components/ui.js";

/**
 * Route map after the functional scope's screen list (docs/product/04-functional-scope.md §3).
 * Paths are stable so links and the PWA start_url can rely on them.
 */

function Root() {
  return (
    <SessionProvider>
      <ToastProvider>
        <Outlet />
      </ToastProvider>
    </SessionProvider>
  );
}

function RequireSession() {
  const { status, branchChosen } = useSession();
  const location = useLocation();
  if (status === "loading") return <Loading />;
  if (status === "anonymous") return <Navigate to="/iniciar-sesion" replace state={{ from: location.pathname }} />;
  if (!branchChosen) return <Navigate to="/sucursal" replace state={{ from: location.pathname }} />;
  return <Shell />;
}

function RequireLogin() {
  const { status } = useSession();
  if (status === "loading") return <Loading />;
  if (status === "anonymous") return <Navigate to="/iniciar-sesion" replace />;
  return <Outlet />;
}

export const router = createBrowserRouter([
  {
    element: <Root />,
    children: [
      { path: "/iniciar-sesion", element: <LoginPage /> },
      { path: "/establecer-contrasena", element: <SetPasswordPage /> },
      { element: <RequireLogin />, children: [{ path: "/sucursal", element: <BranchSelectPage /> }] },
      {
        element: <RequireSession />,
        children: [
          { path: "/", element: <OrdersPage /> },
          { path: "/recepcion", element: <ReceptionPage /> },
          { path: "/solicitudes/:id", element: <OrderPage /> },
          { path: "/cotizaciones", element: <QuotesPage /> },
          { path: "/cotizaciones/nueva", element: <QuoteNewPage /> },
          { path: "/cotizaciones/:id", element: <QuotePage /> },
          { path: "/pacientes", element: <PatientsPage /> },
          { path: "/pacientes/:id", element: <PatientPage /> },
          { path: "/muestras", element: <PendingSamplesPage /> },
          { path: "/captura", element: <CapturePage /> },
          { path: "/captura/solicitud/:orderId", element: <CaptureOrderPage /> },
          { path: "/captura/:itemId", element: <CaptureItemPage /> },
          { path: "/validacion", element: <ValidationPage /> },
          { path: "/validacion/:orderId", element: <ValidationOrderPage /> },
          { path: "/entrega", element: <DeliveryPage /> },
          { path: "/entrega/:orderId", element: <DeliveryOrderPage /> },
          { path: "/calendario", element: <CalendarPage /> },
          { path: "/inicio", element: <HomePage /> },
          { path: "/caja", element: <CashClosePage /> },
          { path: "/reportes/medicos", element: <DoctorReportPage /> },
          { path: "/catalogo", element: <CataloguePage /> },
          { path: "/catalogo/estudios/:id", element: <StudyPage /> },
          { path: "/configuracion", element: <SettingsPage /> },
          { path: "/mi-firma", element: <MySignaturePage /> },
          { path: "*", element: <Empty icon="warning" title="Pantalla no encontrada" hint="Revisa la liga o vuelve al inicio." /> },
        ],
      },
    ],
  },
]);
